import React, { useContext, useMemo, useState } from "react";
import "../css/shop.scss";
import { Link, useSearchParams } from "react-router-dom";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import { FiHeart, FiSearch, FiSliders, FiX } from "react-icons/fi";
import { formatPrice } from "../config/siteConfig";

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { wishlist, toggleWishlist } = useContext(CartContext);

  const query = searchParams.get("q") || "";
  const category = searchParams.get("category") || "All";
  const fit = searchParams.get("fit") || "All";
  const sort = searchParams.get("sort") || "featured";
  const wishlistOnly = searchParams.get("wishlist") === "1";
  const categories = ["All", ...new Set(productData.map((item) => item.category))];
  const fits = ["All", ...new Set(productData.map((item) => item.attributes.fit))];

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    const shouldDelete = value === "" || value === "All" || value === "featured";
    if (shouldDelete) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const products = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const result = productData.filter((product) => {
      const matchesQuery =
        !normalizedQuery ||
        [
          product.name,
          product.category,
          product.color,
          product.attributes.fit,
          product.sku,
          ...product.tags,
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      const matchesCategory = category === "All" || product.category === category;
      const matchesFit = fit === "All" || product.attributes.fit === fit;
      const matchesWishlist = !wishlistOnly || wishlist.includes(product.id);
      return matchesQuery && matchesCategory && matchesFit && matchesWishlist;
    });

    return [...result].sort((a, b) => {
      if (sort === "price-low") return a.price - b.price;
      if (sort === "price-high") return b.price - a.price;
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "new") return b.id - a.id;
      return a.id - b.id;
    });
  }, [category, fit, query, sort, wishlist, wishlistOnly]);

  const clearFilters = () => setSearchParams({}, { replace: true });
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="shop app-page-enter">
      <section className="shop-hero">
        <p className="eyebrow">Spring / Summer 2026</p>
        <h1 className="display-title">
          The right pair,<br />for every day.
        </h1>
        <p>
          Van super-stretch denim tot onze veelzijdige chino. Ontdek 24 broeken, elk
          ontworpen rond pasvorm, comfort en eerlijke materialen.
        </p>
      </section>

      <section className="shop-toolbar" aria-label="Collection controls">
        <div className="shop-toolbar__categories" aria-label="Product categories">
          {categories.map((item) => (
            <button
              type="button"
              key={item}
              className={category === item ? "is-active" : ""}
              onClick={() => setFilter("category", item)}
            >
              {item}
            </button>
          ))}
        </div>
        <button
          className="shop-toolbar__filter-button"
          type="button"
          onClick={() => setFiltersOpen((open) => !open)}
          aria-expanded={filtersOpen}
        >
          <FiSliders aria-hidden="true" /> Filter & sort
        </button>
        <div className="shop-toolbar__sort">
          <label htmlFor="sort-products">Sort by</label>
          <select
            id="sort-products"
            value={sort}
            onChange={(event) => setFilter("sort", event.target.value)}
          >
            <option value="featured">Featured</option>
            <option value="new">Newest</option>
            <option value="price-low">Price, low to high</option>
            <option value="price-high">Price, high to low</option>
            <option value="name">Name</option>
          </select>
        </div>
      </section>

      <section className={`shop-filters ${filtersOpen ? "is-open" : ""}`}>
        <button
          type="button"
          className="shop-filters__close"
          onClick={() => setFiltersOpen(false)}
          aria-label="Close filters"
        >
          <FiX aria-hidden="true" />
        </button>
        <div className="shop-filters__search">
          <label htmlFor="collection-search">Zoeken</label>
          <div>
            <FiSearch aria-hidden="true" />
            <input
              id="collection-search"
              value={query}
              onChange={(event) => setFilter("q", event.target.value)}
              placeholder="Model, kleur of code"
            />
          </div>
        </div>
        <div>
          <label htmlFor="fit-products">Fit</label>
          <select
            id="fit-products"
            value={fit}
            onChange={(event) => setFilter("fit", event.target.value)}
          >
            {fits.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </div>
        <label className="shop-filters__wishlist">
          <input
            type="checkbox"
            checked={wishlistOnly}
            onChange={(event) => setFilter("wishlist", event.target.checked ? "1" : "")}
          />
          Alleen favorieten
        </label>
        <button className="text-link" type="button" onClick={clearFilters}>
          Reset filters
        </button>
      </section>

      <div className="shop-results">
        <p>{products.length} producten</p>
        {(query || category !== "All" || fit !== "All" || wishlistOnly) && (
          <button type="button" onClick={clearFilters}>Wis selectie</button>
        )}
      </div>

      {products.length > 0 ? (
        <section className="product-grid" aria-label="Products">
          {products.map((product) => {
            const isSaved = wishlist.includes(product.id);
            return (
              <article className="product-card" key={product.id}>
                <div className="product-card__image">
                  <Link to={`/shopDetails/${product.id}`} onClick={scrollTop}>
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                      onError={(event) => {
                        event.currentTarget.src = "/main/ss26-hero.jpg";
                      }}
                    />
                  </Link>
                  {product.collection === "SS26" && <span className="product-card__badge">New</span>}
                  <button
                    type="button"
                    className={isSaved ? "is-saved" : ""}
                    onClick={() => toggleWishlist(product.id)}
                    aria-label={`${isSaved ? "Remove" : "Add"} ${product.name} ${isSaved ? "from" : "to"} wishlist`}
                  >
                    <FiHeart aria-hidden="true" />
                  </button>
                </div>
                <Link className="product-card__info" to={`/shopDetails/${product.id}`} onClick={scrollTop}>
                  <div>
                    <p>{product.category} / {product.attributes.fit} fit</p>
                    <h2>{product.name}</h2>
                    <span>{product.color}</span>
                  </div>
                  <strong>{formatPrice(product.price)}</strong>
                </Link>
              </article>
            );
          })}
        </section>
      ) : (
        <section className="shop-empty" role="status">
          <p className="eyebrow">No perfect pair yet</p>
          <h2>Geen producten gevonden.</h2>
          <p>Probeer een andere zoekterm of maak de filters leeg.</p>
          <button className="solid-button" type="button" onClick={clearFilters}>
            Bekijk de hele collectie
          </button>
        </section>
      )}
    </main>
  );
}

export default Shop;
